import { BlissSVGBuilder } from "bliss-svg-builder";
import { css, html, LitElement } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { redoIcon, trashIcon, undoIcon } from "@blissary/shell-components/icons";
import "./movable-shape.js";

import "@awesome.me/webawesome/dist/components/button/button.js";

// Extract per-shape BlissSVGBuilder options from a shape code.
// Handles all three sub-global levels:
//   "[color=red]|C8"  → word level   { options: { color: "red" }, bareCode: "C8" }
//   "[color=red]C8"   → char level   { options: { color: "red" }, bareCode: "C8" }
//   "[color=red]>C8"  → shape level  { options: { color: "red" }, bareCode: "C8" }
//   "C8"              → no options    { options: {}, bareCode: "C8" }
function extractShapeOptions(shapeCode) {
  const options = {};
  let rest = shapeCode;
  // Strip all leading [opts] brackets (word/char/shape levels) and collect options.
  // e.g. "[color=blue][color=red]>C8" → options merged, bareCode "C8"
  while (true) {
    const match = rest.match(/^\[([^\]]*)\][|>]?(.+)$/);
    if (!match) break;
    for (const pair of match[1].split(";")) {
      const eqIdx = pair.indexOf("=");
      if (eqIdx > 0) options[pair.slice(0, eqIdx).trim()] = pair.slice(eqIdx + 1).trim();
    }
    rest = match[2];
  }
  return { options, bareCode: rest };
}

// Extract group-level option brackets (char/word level, not shape-level) from a
// shape code.  These brackets scope to all shapes in the group, not just one.
// "[color=blue][color=red]>C8" → { groupPrefix: "[color=blue]", remainder: "[color=red]>C8" }
// "[color=red]>C8"             → { groupPrefix: "",              remainder: "[color=red]>C8" }
// "[color=blue]|C8"            → { groupPrefix: "[color=blue]|", remainder: "C8" }
// "C8"                         → { groupPrefix: "",              remainder: "C8" }
function extractGroupPrefix(shapeCode) {
  let groupPrefix = "";
  let rest = shapeCode;
  while (true) {
    const match = rest.match(/^(\[[^\]]*\])([|>]?)(.+)$/);
    if (!match) break;
    if (match[2] === ">") break; // shape-level — stays with this shape only
    groupPrefix += match[1] + match[2];
    rest = match[3];
  }
  return { groupPrefix, remainder: rest };
}

// Parse a code entry like "C8:1,1" into { shapeCode: "C8", x: 1, y: 1 }
// Returns null if the entry doesn't have explicit coordinates.
function parseCodeEntry(entry) {
  const match = entry.match(/^(.+?):(-?[\d.]+),(-?[\d.]+)$/);
  if (match) {
    return { shapeCode: match[1], x: Number(match[2]), y: Number(match[3]) };
  }
  return null;
}

class EditableBoard extends LitElement {
  static get properties() {
    return {
      _grid: {
        type: Object,
      },
      _bliss: {
        type: Object,
      },
      _shapeSvgs: {
        type: Array,
      },
      _temp: {
        type: Object,
      },
      _codes: {
        type: Array,
      },
      _history: {
        type: Object,
      },
      strokeWidth: {
        type: Number,
      },
      backgroundColor: {
        type: String,
      },
      gridSkyColor: {
        type: String,
      },
      gridEarthColor: {
        type: String,
      },
      gridMajorColor: {
        type: String,
      },
      gridMediumColor: {
        type: String,
      },
      gridMinorColor: {
        type: String,
      },
      snapStep: {
        type: Number,
      },
      _dimensions: {
        type: Object,
      },
    };
  }

  constructor() {
    super();
    this._codes = [];
    this._inlineOptions = {};
    this._shapeSvgs = [];
    this._useIndividualShapes = false;
    this.snapStep = 1;
    this._movableShapeData = {};
    this._shapeMoving = false;
    this._dimensions = {
      width: 0,
      height: 20,
      margin: 1 / 6,
    };
    this._history = {
      currentIndex: -1,
      fullCodes: [],
      add: function (code) {
        this.fullCodes.splice(++this.currentIndex);
        this.fullCodes.push(JSON.parse(JSON.stringify(code)));
        this.canRedo = false;
        this.canUndo = true;
      },
      clear: function (_code) {
        this.currentIndex = -1;
        this.fullCodes = [];
      },
      canRedo: false,
      canUndo: false,
      redo: function () {
        if (this.canRedo) {
          this.currentIndex++;
          if (this.currentIndex >= this.fullCodes.length - 1) {
            this.canRedo = false;
          }
          this.canUndo = true;
        }
      },
      undo: function () {
        if (this.canUndo) {
          this.currentIndex--;
          if (this.currentIndex === -1) {
            this.canUndo = false;
          }
          this.canRedo = true;
        }
      },
      getCurrentCodes: function () {
        if (typeof this.fullCodes[this.currentIndex] !== "undefined") {
          return JSON.parse(JSON.stringify(this.fullCodes[this.currentIndex]));
        } else {
          return [];
        }
      },
    };

    this.setBlissCodeFromUrl();
  }

  get dimensions() {
    return this._dimensions;
  }

  // Pixel multiplier: 1 bliss unit = 12 pixels on the board
  get sizeMultiplier() {
    return 12;
  }

  // Board margin in bliss units (matches margin param in BlissSVGBuilder calls)
  get svgMargin() {
    return 0.75;
  }

  dimensionsChanged(width, height) {
    this._dimensions = {
      width: width,
      height: height,
      margin:
        Math.round((this.strokeWidth / 2 + (this.strokeWidth > 1 / 3 ? 1 / 3 : this.strokeWidth) + 1 / 6) * 3) / 3 -
        1 / 6,
    };
    const dimensionsChangedEvent = new CustomEvent("dimensions-changed", {
      detail: { message: "dimensions changed.", dimensions: this._dimensions },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(dimensionsChangedEvent);
  }

  // Build the options string for BlissSVGBuilder, merging inline options with UI options.
  // UI options are used only for keys not already set by inline options.
  // Global-only options that apply at the board level, not per-shape
  static BOARD_LEVEL_OPTIONS = new Set(["background", "grid"]);

  _buildOptionsString(extraOptions = {}, { excludeBoardOptions = false } = {}) {
    const uiOptions = {
      "stroke-width": this.strokeWidth,
      grid: 0,
      margin: 0.75,
      ...extraOptions,
    };
    // Inline options take precedence over UI options
    const merged = { ...uiOptions, ...this._inlineOptions };
    // When rendering individual shapes, strip board-level options
    if (excludeBoardOptions) {
      for (const key of EditableBoard.BOARD_LEVEL_OPTIONS) {
        delete merged[key];
      }
    }
    return Object.entries(merged)
      .map(([k, v]) => `${k}=${v}`)
      .join(";");
  }

  // Check if all _codes entries have explicit coordinates and can be rendered individually
  _canRenderIndividually() {
    if (this.isComposition) return false;
    if (this._codes.length === 0) return false;
    return this._codes.every((entry) => parseCodeEntry(entry) !== null);
  }

  // Render the bliss content — either as individual shape SVGs or one monolithic SVG
  _renderBliss() {
    this._rebuildGrid();
    if (this._canRenderIndividually()) {
      this._renderIndividualShapes();
    } else {
      this._renderMonolithic();
    }
  }

  // Inject stroke-width into every leading per-shape option bracket in a code entry
  // so it propagates through BlissSVGBuilder's element tree at all levels.
  // Matches consecutive [opts] blocks at the start, each optionally followed by | or >.
  _injectStrokeWidth(code) {
    return code.replace(/^(\[([^\]]*)\][|>]?)+/, (leading) => {
      return leading.replace(/\[([^\]]*)\]/g, (_, opts) => {
        const pairs = opts.split(";").filter(Boolean);
        if (!pairs.some(p => p.startsWith("stroke-width="))) {
          pairs.unshift(`stroke-width=${this.strokeWidth}`);
        }
        return `[${pairs.join(";")}]`;
      });
    });
  }

  // Render all shapes as one monolithic SVG (composition mode or fallback)
  _renderMonolithic() {
    this._useIndividualShapes = false;
    this._shapeSvgs = [];

    // Inject stroke-width into per-shape option brackets so it propagates
    // through BlissSVGBuilder's element tree (global options don't inherit).
    const charCodes = this._codes.map(code => this._injectStrokeWidth(code)).join(";");
    const optStr = this._buildOptionsString();
    const bt = new BlissSVGBuilder(`[${optStr}]||${charCodes}`);
    const svgEl = bt.svgElement;
    const viewBox = svgEl.getAttribute("viewBox").split(" ").map(Number);

    this.dimensionsChanged(viewBox[2], viewBox[3]);

    svgEl.id = "bliss";
    this._bliss = svgEl;
  }

  // Render each shape as its own SVG element, positioned via CSS.
  // Each shape gets two SVGs: a wide (hit target) and a normal (visible).
  // Uses freestyle=1 like shape-board/movable-shape for correct viewBox with margin.
  _renderIndividualShapes() {
    this._useIndividualShapes = true;
    this._bliss = null;

    const sm = this.sizeMultiplier;
    const margin = this.svgMargin;
    const shapes = [];
    const hitStrokeWidth = 1.5;
    // The host has padding which offsets static-positioned elements (#grid).
    // Explicit left/top on absolutely-positioned elements is relative to the
    // padding edge (border-inner), so we must add the padding to align with
    // the grid SVG which sits at its static position (after padding).
    const hostPadding = 4;

    // Extract group-level brackets (char/word level) from the first entry
    // that has them — these apply to ALL shapes, not just the first.
    // e.g. "[color=blue][color=red]>C8:0,8;C4:3,2" → groupPrefix "[color=blue]"
    let groupPrefix = "";
    for (const code of this._codes) {
      const parsed0 = parseCodeEntry(code);
      if (parsed0) {
        ({ groupPrefix } = extractGroupPrefix(parsed0.shapeCode));
        break;
      }
    }

    for (let i = 0; i < this._codes.length; i++) {
      const parsed = parseCodeEntry(this._codes[i]);
      if (!parsed) continue;

      // For the first entry, use its own brackets as-is (includes group + shape level).
      // For subsequent entries, prepend the group-level prefix so they inherit context.
      const fullShapeCode = i === 0 ? parsed.shapeCode : groupPrefix + parsed.shapeCode;
      const injectedShapeCode = this._injectStrokeWidth(fullShapeCode);
      const { bareCode } = extractShapeOptions(parsed.shapeCode);
      const shapeOpts = { "stroke-width": this.strokeWidth, grid: 0, freestyle: 1 };
      for (const [k, v] of Object.entries(this._inlineOptions)) {
        if (!EditableBoard.BOARD_LEVEL_OPTIONS.has(k)) shapeOpts[k] = v;
      }
      const normalOptStr = Object.entries(shapeOpts).map(([k, v]) => `${k}=${v}`).join(";");
      const normalSvg = new BlissSVGBuilder(
        `[${normalOptStr}]||${injectedShapeCode}`
      ).svgElement;
      const [nvx, nvy, nvw, nvh] = normalSvg.getAttribute("viewBox").split(" ").map(Number);

      // Wide (hit target) SVG — bare shape code (already stripped by extractShapeOptions)
      // with larger stroke for easier grabbing, matching shape-board's pattern
      const hitSvg = new BlissSVGBuilder(
        `[grid=0;stroke-width=${hitStrokeWidth};freestyle=1]||${bareCode}`
      ).svgElement;
      const [hvx, hvy, hvw, hvh] = hitSvg.getAttribute("viewBox").split(" ").map(Number);
      const gridWidth = hvw + hvx * 2;
      const gridHeight = hvh + hvy * 2;

      // Store data attributes for movable-shape (same pattern as shape-board)
      hitSvg.setAttribute("data-width", gridWidth * sm);
      hitSvg.setAttribute("data-height", gridHeight * sm);
      hitSvg.setAttribute("data-internal-x", nvx * sm);
      hitSvg.setAttribute("data-internal-y", nvy * sm);
      hitSvg.setAttribute("data-code", parsed.shapeCode);
      hitSvg.setAttribute("data-index", i);

      // Position using same math as shape-board: (viewBoxX + positionX) * size
      // Plus margin offset to align with the grid SVG, plus host padding
      shapes.push({
        normalSvg,
        hitSvg,
        normal: {
          pxLeft: (nvx + parsed.x + margin) * sm + hostPadding,
          pxTop: (nvy + parsed.y + margin) * sm + hostPadding,
          pxWidth: nvw * sm,
          pxHeight: nvh * sm,
        },
        hit: {
          pxLeft: (hvx + parsed.x + margin) * sm + hostPadding,
          pxTop: (hvy + parsed.y + margin) * sm + hostPadding,
          pxWidth: hvw * sm,
          pxHeight: hvh * sm,
        },
        index: i,
      });
    }

    this._shapeSvgs = shapes;

    // Still need overall dimensions for export sizing
    const charCodes = this._codes.map(code => this._injectStrokeWidth(code)).join(";");
    const optStr = this._buildOptionsString();
    const bt = new BlissSVGBuilder(`[${optStr}]||${charCodes}`);
    const viewBox = bt.svgElement.getAttribute("viewBox").split(" ").map(Number);
    this.dimensionsChanged(viewBox[2], viewBox[3]);
  }

  // Rebuild the grid SVG, applying inline background/grid overrides if present
  _rebuildGrid() {
    const bg = this._inlineOptions.background ?? this.backgroundColor;
    const showGrid = "grid" in this._inlineOptions ? this._inlineOptions.grid !== "0" : true;
    const grid = new BlissSVGBuilder(
      `[grid=${showGrid ? "1" : "0"};background=${bg};grid-sky-color=${this.gridSkyColor};grid-earth-color=${this.gridEarthColor};grid-major-color=${this.gridMajorColor};grid-medium-color=${this.gridMediumColor};grid-minor-color=${this.gridMinorColor};grid-stroke-width=0.166;margin=0.75;min-width=56]||`,
    ).svgElement;
    grid.id = "grid";
    this._grid = grid;
  }

  async updated(changedProperties) {
    if (changedProperties.has("strokeWidth")) {
      this._renderBliss();
    }
    if (
      changedProperties.has("backgroundColor") ||
      changedProperties.has("gridSkyColor") ||
      changedProperties.has("gridEarthColor") ||
      changedProperties.has("gridMajorColor") ||
      changedProperties.has("gridMediumColor") ||
      changedProperties.has("gridMinorColor")
    ) {
      this._rebuildGrid();
    }
  }

  async firstUpdated() {
    // Event delegation for shape drag-to-reposition (same pattern as shape-board)
    this.shadowRoot.addEventListener("pointerdown", (event) => {
      const leftMouseButtonOnlyDown = event.buttons === undefined ? event.which === 1 : event.buttons === 1;
      if (leftMouseButtonOnlyDown && event.target.nodeName === "path") {
        const svgElement = event.target.closest("svg");
        if (svgElement?.classList.contains("shape-hit")) {
          const index = Number(svgElement.getAttribute("data-index"));
          this._handleShapePointerDown(event, index);
        }
      }
    });

    this._rebuildGrid();

    const bt = new BlissSVGBuilder("[grid=0;margin=0.75]||");
    const svgEl = bt.svgElement;
    const viewBox = svgEl.getAttribute("viewBox").split(" ").map(Number);

    this.dimensionsChanged(viewBox[2], viewBox[3]);

    svgEl.id = "bliss";
    this._bliss = svgEl;

    const temp = new BlissSVGBuilder("[grid=0;margin=0.75]||").svgElement;
    temp.id = "temp";
    this._temp = temp;
  }

  getBlissCode() {
    const codeBody = this._codes.join(";");
    if (this._inlineOptions && Object.keys(this._inlineOptions).length > 0) {
      const optStr = Object.entries(this._inlineOptions)
        .map(([k, v]) => `${k}=${v}`)
        .join(";");
      return `[${optStr}]||${codeBody}`;
    }
    return codeBody;
  }

  // Dispatch input state to parent (valid/invalid, inline options, composition mode)
  _dispatchInputState(valid) {
    this.dispatchEvent(new CustomEvent("input-state-changed", {
      detail: {
        valid,
        inlineOptions: { ...this._inlineOptions },
        isComposition: this.isComposition,
      },
      bubbles: true,
      composed: true,
    }));
  }

  // Parse "[key=val;key2=val2]||codeBody" into { options, codeBody }
  _parseInputString(input) {
    const match = input.match(/^\[([^\]]*)\]\|\|(.*)$/);
    if (match) {
      const options = {};
      for (const pair of match[1].split(";")) {
        const eqIdx = pair.indexOf("=");
        if (eqIdx > 0) {
          options[pair.slice(0, eqIdx).trim()] = pair.slice(eqIdx + 1).trim();
        }
      }
      return { options, codeBody: match[2] };
    }
    return { options: {}, codeBody: input };
  }

  // Check if the current code contains composition delimiters (// or /)
  get isComposition() {
    const code = this._codes.join(";");
    return /\//.test(code);
  }

  setBlissCode(code) {
    const trimmed = code.trim();

    // Empty string is valid — clear the board
    if (!trimmed) {
      this._inlineOptions = {};
      this._codes = [];
      this._history.clear();
      this._renderBliss();
      this.updateHistoryState();
      this._dispatchInputState(true);
      return;
    }

    // Validate by attempting to build
    try {
      new BlissSVGBuilder(trimmed);
    } catch (_e) {
      this._dispatchInputState(false);
      return;
    }

    // Parse options and code body
    const { options, codeBody } = this._parseInputString(trimmed);
    this._inlineOptions = options;

    // For compositions (contains /), store as a single entry to preserve delimiters
    if (/\//.test(codeBody)) {
      this._codes = [codeBody];
    } else {
      this._codes = codeBody ? codeBody.split(";").filter(Boolean) : [];
    }

    this._history.clear();
    if (this._codes.length > 0) {
      this._history.add(this._codes);
    }

    this._renderBliss();
    this.updateHistoryState();
    this._dispatchInputState(true);
  }

  setBlissCodeFromUrl() {
    // Use the raw URL to extract the code, treating # as literal (not fragment).
    // This allows unencoded # in color values like ?c=[background=#4D723D]||C8:0,8
    const raw = window.location.href;
    const qIdx = raw.indexOf("?");
    if (qIdx === -1) return;
    const params = new URLSearchParams(raw.slice(qIdx + 1));
    const code = params.get("c");
    if (code) {
      this.setBlissCode(code);
    }
  }

  updateHistoryState() {
    // Encode only # as %23 to keep URLs readable while preventing fragment interpretation
    const newUrl = `${window.location.href.split("?")[0]}?c=${this.getBlissCode().replace(/#/g, "%23")}`;
    window.history.replaceState({}, document.title, newUrl);

    // Dispatch custom event to notify URL has changed
    const urlChangedEvent = new CustomEvent("url-changed", {
      detail: { url: window.location.search },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(urlChangedEvent);
  }

  addShape(code, x, y, snapStep) {
    // Block drag-and-drop for compositions
    if (this.isComposition) return;

    const grid = this.shadowRoot.getElementById("grid");
    const rect = grid.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();
    const scale = hostRect.width / this.offsetWidth || 1;
    const sm = this.sizeMultiplier;

    let internalX = (x - rect.x) / scale / sm - this.svgMargin;
    let internalY = (y - rect.y) / scale / sm - this.svgMargin;

    if (snapStep) {
      internalX = Math.round(internalX / snapStep) * snapStep;
      internalY = Math.round(internalY / snapStep) * snapStep;
    } else {
      internalX = Math.round(internalX / 0.01) * 0.01;
      internalY = Math.round(internalY / 0.01) * 0.01;
    }

    if (/^X/.test(code)) {
      internalY = 0;
    }

    const newCode = `${code}:${internalX},${internalY}`;
    this._codes.push(newCode);
    this._history.add(this._codes);
    this._renderBliss();
    this.updateHistoryState();
  }

  removeShape(_shape) {
    console.log("shape removed in editable-board (not implemented)");
  }

  clearShapes() {
    this._codes = [];
    this._inlineOptions = {};
    this._history.add(this._codes);
    this._renderBliss();
    this.updateHistoryState();
  }

  showTempShape(code, x, y, snapStep) {
    // Block temp shape for compositions
    if (this.isComposition) return;

    const grid = this.shadowRoot.getElementById("grid");
    const rect = grid.getBoundingClientRect();
    const hostRect = this.getBoundingClientRect();
    const scale = hostRect.width / this.offsetWidth || 1;
    const sm = this.sizeMultiplier;

    let internalX = (x - rect.x) / scale / sm - this.svgMargin;
    let internalY = (y - rect.y) / scale / sm - this.svgMargin;

    if (snapStep) {
      internalX = Math.round(internalX / snapStep) * snapStep;
      internalY = Math.round(internalY / snapStep) * snapStep;
    } else {
      internalX = Math.round(internalX / 0.01) * 0.01;
      internalY = Math.round(internalY / 0.01) * 0.01;
    }

    if (/^X/.test(code)) {
      internalY = 0;
    }

    const temp = new BlissSVGBuilder(`[stroke-width=0.33;grid=0;margin=0.75]||${code}:${internalX},${internalY}`)
      .svgElement;
    temp.id = "temp";

    if (/^X/.test(code)) {
      const pathEl = temp.children[1];
      if (pathEl?.children[0]) {
        pathEl.children[0].style.fill = "var(--bl-color-primary)";
      }
    }

    this._temp = temp;
  }

  hideTempShape() {
    if (this._temp.innerHTML) {
      this._temp.innerHTML = "";
    }
  }

  async handleClickedClear(_e) {
    this.clearShapes();
  }

  async handleClickedUndo(_e) {
    this._history.undo();
    this._codes = this._history.getCurrentCodes();
    this._renderBliss();
    this.updateHistoryState();
  }

  async handleClickedRedo(_e) {
    this._history.redo();
    this._codes = this._history.getCurrentCodes();
    this._renderBliss();
    this.updateHistoryState();
  }

  // --- Drag-to-reposition via movable-shape ---

  _handleShapePointerDown(event, shapeIndex) {
    const svgElement = event.target.closest("svg");
    if (!svgElement) return;

    const svgRect = svgElement.getBoundingClientRect();
    const internalX = Number(svgElement.getAttribute("data-internal-x"));
    const internalY = Number(svgElement.getAttribute("data-internal-y"));
    // The hit SVG (wider stroke) may have a different viewBox origin than the
    // normal SVG for shapes with degenerate dimensions (e.g. HL8's vertical
    // extent IS the stroke, so stroke-width shifts the viewBox origin).
    // Adjust the position from hit origin to normal origin so the movable-shape
    // aligns with the visible shape, not the wider hit target.
    const [hvx, hvy] = svgElement.getAttribute("viewBox").split(" ").map(Number);
    const sm = this.sizeMultiplier;
    const hostRect = this.getBoundingClientRect();
    const scale = hostRect.width / this.offsetWidth || 1;
    this._movableShapeData = {
      width: Number(svgElement.getAttribute("data-width")),
      height: Number(svgElement.getAttribute("data-height")),
      internalX,
      internalY,
      x: svgRect.left + (internalX - hvx * sm) * scale,
      y: svgRect.top + (internalY - hvy * sm) * scale,
      code: svgElement.getAttribute("data-code"),
      pointerX: event.clientX,
      pointerY: event.clientY,
    };
    document.body.style.cursor = "grabbing";
    this._shapeMoving = true;
    this.setAttribute("shape-dragging", "");

    // Track pointer for trash zone detection and highlight
    this._lastPointerY = event.clientY;
    this._pointerMoveHandler = (ev) => {
      this._lastPointerY = ev.clientY;
      const boardRect = this.getBoundingClientRect();
      if (ev.clientY > boardRect.bottom) {
        this.setAttribute("over-trash", "");
      } else {
        this.removeAttribute("over-trash");
      }
    };
    window.addEventListener("pointermove", this._pointerMoveHandler);

    // Remove the shape from _codes so it disappears while being dragged
    this._dragRemovedIndex = shapeIndex;
    this._dragRemovedCode = this._codes[shapeIndex];
    this._codes.splice(shapeIndex, 1);
    this._renderBliss();
  }

  handleRepositionDropped(e) {
    document.body.style.cursor = "";
    this._shapeMoving = false;

    const { x, y } = e.detail;
    const boardRect = this.getBoundingClientRect();

    // Clean up pointer tracking
    if (this._pointerMoveHandler) {
      window.removeEventListener("pointermove", this._pointerMoveHandler);
      this._pointerMoveHandler = null;
    }

    // Trash zone is below the board (50% width, centered, 42px tall).
    // Use actual pointer Y position, not shape content position.
    if (this._lastPointerY > boardRect.bottom) {
      // Dropped on trash — shape already removed from _codes, just commit
      e.stopPropagation(); // prevent bliss-maker from re-adding via addShape
      this._history.add(this._codes);
      this._renderBliss();
      this.hideTempShape();
      this.updateHistoryState();
      this._dragRemovedIndex = null;
      this._dragRemovedCode = null;
      this.removeAttribute("shape-dragging");
      this.removeAttribute("over-trash");
      return;
    }

    this.removeAttribute("shape-dragging");
    this.removeAttribute("over-trash");

    // Check if dropped inside the board
    if (x > boardRect.x && x < boardRect.x + boardRect.width && y > boardRect.y && y < boardRect.y + boardRect.height) {
      // Dropped inside — let event bubble to bliss-maker's handleShapeDropped → addShape()
    } else {
      // Dropped outside — restore the original shape
      e.stopPropagation();
      this._codes.splice(this._dragRemovedIndex, 0, this._dragRemovedCode);
      this._renderBliss();
      this.hideTempShape();
      this.updateHistoryState();
    }

    this._movableShapeData = {}; // clear the dotted drag shape
    this._dragRemovedIndex = null;
    this._dragRemovedCode = null;
  }

  handleRepositionMoved(e) {
    // Reuse the existing showTempShape/hideTempShape via bliss-maker's handleShapeMoved
    // The event bubbles up — nothing extra needed here
  }

  static get styles() {
    return css`
      :host {
        display: inline-block;
        position: relative;
        touch-action: none; /* necessary for dragging shapes on mobile */
        width: 700px;
        height: 268px;
        padding: 4px;
        z-index: 4;
        overflow: hidden;
        border: 1px solid var(--bl-color-border);
        border-radius: var(--bl-radius-md);
        box-sizing: border-box;
      }

      #grid, #bliss, #temp {
        height: 258px;
        width: auto;
        position: absolute;
        pointer-events: none;
      }

      .shape-normal {
        position: absolute;
        pointer-events: none;
      }

      .shape-hit {
        position: absolute;
        stroke: rgba(0, 0, 0, 0.001);
        pointer-events: none;
      }

      .shape-hit > path,
      .shape-hit > g > path {
        pointer-events: stroke;
        cursor: grab;
      }

      .shape-hit:hover {
        stroke: rgba(0, 0, 0, 0.15);
      }

      .shape-hit > path:active,
      .shape-hit > g > path:active {
        cursor: grabbing;
      }

      #temp > path:last-of-type {
        stroke-dasharray: 0.4 0.55;
        stroke: var(--bl-color-primary);
      }

      #grid > path, #bliss > path, #temp > path {
        pointer-events: stroke;
      }

      wa-button[label] {
        float: right;
        margin-top: 6px;
        position: relative;
        z-index: 10;
        opacity: 0.5;
        transition: opacity 150ms ease;
      }

      wa-button[label]:hover:not([disabled]) {
        opacity: 1;
      }

      wa-button[label][disabled] {
        opacity: 0;
        pointer-events: none;
      }

      #trash-zone {
        display: none;
        position: absolute;
        bottom: -46px;
        left: 25%;
        width: 50%;
        height: 42px;
        z-index: 20;
        align-items: center;
        justify-content: center;
        background: rgba(200, 50, 50, 0.12);
        border: 2px dashed rgba(200, 50, 50, 0.4);
        border-top: none;
        border-radius: 0 0 var(--bl-radius-md) var(--bl-radius-md);
        pointer-events: none;
      }

      :host([shape-dragging]) {
        overflow: visible;
      }

      :host([shape-dragging]) #trash-zone {
        display: flex;
      }

      #trash-zone .icon svg {
        stroke: rgba(200, 50, 50, 0.6);
      }

      :host([over-trash]) #trash-zone {
        background: rgba(200, 50, 50, 0.25);
        border-color: rgba(200, 50, 50, 0.7);
      }

      wa-button[label]::part(base) {
        padding: 4px;
        min-height: 34px;
        background: none;
      }

      wa-button[label]::part(base):hover {
        background: none;
      }

      .icon {
        display: inline-flex;
        width: 24px;
        height: 24px;
        align-items: center;
        justify-content: center;
      }

      .icon svg {
        width: 100%;
        height: 100%;
        stroke: var(--bl-color-primary);
      }

      #clear {
        margin-right: 8px;
      }
    `;
  }

  _renderShapeSvg(shape) {
    // Normal SVG — visible shape, no pointer events
    const normal = shape.normalSvg;
    normal.style.cssText = `
      position: absolute;
      left: ${shape.normal.pxLeft}px;
      top: ${shape.normal.pxTop}px;
      width: ${shape.normal.pxWidth}px;
      height: ${shape.normal.pxHeight}px;
      pointer-events: none;
    `;
    normal.classList.add("shape-normal");

    // Hit target SVG — wide stroke, nearly invisible, receives pointer events
    const hit = shape.hitSvg;
    hit.style.cssText = `
      position: absolute;
      left: ${shape.hit.pxLeft}px;
      top: ${shape.hit.pxTop}px;
      width: ${shape.hit.pxWidth}px;
      height: ${shape.hit.pxHeight}px;
    `;
    hit.classList.add("shape-hit");
    hit.setAttribute("data-index", shape.index);

    return html`${normal}${hit}`;
  }

  render() {
    return html`
      ${this._grid}
      ${this._useIndividualShapes
        ? this._shapeSvgs.map((shape) => this._renderShapeSvg(shape))
        : this._bliss}
      ${this._temp}
      <div id="trash-zone">
        <span class="icon">${unsafeHTML(trashIcon)}</span>
      </div>
      <wa-button id="clear" appearance="plain" label="Clear" @click="${this.handleClickedClear}" ?disabled=${this._codes.length === 0}>
        <span class="icon">${unsafeHTML(trashIcon)}</span>
      </wa-button>
      <wa-button id="redo" appearance="plain" label="Redo" @click="${this.handleClickedRedo}" ?disabled=${!this._history.canRedo}>
        <span class="icon">${unsafeHTML(redoIcon)}</span>
      </wa-button>
      <wa-button id="undo" appearance="plain" label="Undo" @click="${this.handleClickedUndo}" ?disabled=${!this._history.canUndo}>
        <span class="icon">${unsafeHTML(undoIcon)}</span>
      </wa-button>
      <movable-shape
        @shape-moved="${this.handleRepositionMoved}"
        @shape-dropped="${this.handleRepositionDropped}"
        .data="${this._movableShapeData}">
      </movable-shape>
    `;
  }
}

customElements.define("editable-board", EditableBoard);
